import type { AssigneeSearchResult } from '@auth/entities/account';
import { useAssigneeSearch } from '@auth/entities/account';
import { useCreateTaskFromSet } from '@task-manager/entities/tasks';
import { TaskCollectionSelect } from '@task-manager/features/task-collection-select';
import { useEffect, useMemo, useState } from 'react';
import { trpc } from '@/shared/api/trpc';
import { toastMutation } from '@/shared/lib';
import { useDebouncedValue } from '@/shared/lib/use-debounced-value';
import {
  Button,
  Modal,
  ModalFooter,
  ModalHeader,
  Select,
  ServerSearchSelect,
  Text,
} from '@/shared/ui';
import css from './apply-collection-dialog.module.scss';

type ApplyCollectionDialogProps = {
  open: boolean;
  /** Pre-selected collection. Pass '' to let the user pick inside the dialog. */
  collectionId: string;
  itineraryId?: string;
  onClose: () => void;
};

/**
 * Mirrors the legacy "Add Task Set" modal. Three controls + a preview:
 *
 *  - **Assigned to**: a search-as-you-type account picker (legacy parity)
 *    defaulting to the current user, so the set can be applied to a
 *    teammate. Reuses the create-task form's `useAssigneeSearch`.
 *  - **Itinerary**: search-as-you-type via `trpc.itineraries.search`.
 *    Locked to the drawer's `itineraryId` when applied from there.
 *  - **Collection**: pick-then-apply (`<TaskCollectionSelect>`), only
 *    when the caller didn't pre-pick.
 *  - **Preview**: shows the template tasks in the selected collection
 *    so the user knows what'll be created.
 *
 * On Apply, fires `createFromSet` and closes.
 */
export const ApplyCollectionDialog = ({
  open,
  collectionId: prePickedCollectionId,
  itineraryId: lockedItineraryId,
  onClose,
}: ApplyCollectionDialogProps) => {
  const me = trpc.auth.me.useQuery(undefined, { enabled: open });
  const myAccountId = me.data?.user?.id ?? '';

  const [assignedAccountId, setAssignedAccountId] = useState('');
  const [picked, setPicked] = useState<string | undefined>(undefined);
  const [itinerarySearch, setItinerarySearch] = useState('');
  const [pickedItineraryId, setPickedItineraryId] = useState<
    string | undefined
  >(lockedItineraryId);

  // Seed assignee from current user once auth.me lands.
  useEffect(() => {
    if (myAccountId && !assignedAccountId) setAssignedAccountId(myAccountId);
  }, [myAccountId, assignedAccountId]);

  // Reset when the dialog opens fresh so stale state doesn't bleed over.
  useEffect(() => {
    if (open) {
      setPicked(undefined);
      setPickedItineraryId(lockedItineraryId);
      setItinerarySearch('');
    }
  }, [open, lockedItineraryId]);

  const debouncedItinerarySearch = useDebouncedValue(itinerarySearch, 250);

  const itinerariesQuery = trpc.itineraries.search.useQuery(
    {
      limit: 20,
      offset: 0,
      title: debouncedItinerarySearch || undefined,
    },
    { enabled: open && !lockedItineraryId }
  );

  const collectionId = prePickedCollectionId || picked || '';
  const needsPick = !prePickedCollectionId;

  const templatesQuery = trpc.taskCollections.templateTasks.useQuery(
    { collectionId },
    { enabled: !!collectionId }
  );

  const itineraryOptions = useMemo(() => {
    const opts: Array<{ value: string; label: string }> = [
      { value: '', label: 'No itinerary' },
    ];
    for (const it of itinerariesQuery.data?.data ?? []) {
      opts.push({
        value: it.id,
        label: it.itineraryRef ?? it.title ?? it.id,
      });
    }
    // When locked, include the locked id even if the search hasn't loaded
    // it — render it as a single non-removable option.
    if (lockedItineraryId && !opts.some((o) => o.value === lockedItineraryId)) {
      opts.push({ value: lockedItineraryId, label: lockedItineraryId });
    }
    return opts;
  }, [itinerariesQuery.data, lockedItineraryId]);

  const mutation = useCreateTaskFromSet();

  const handleApply = () => {
    if (!assignedAccountId || !collectionId) return;
    mutation.mutate(
      {
        assignedAccountId,
        collectionId,
        itineraryId: pickedItineraryId || undefined,
        // Tasks cloned from a set are system-created for someone, so they
        // default to the Attention flag (legacy parity / TCT03-002).
        flag: 5,
      },
      toastMutation(
        { success: 'Tasks added', error: null },
        {
          onSuccess: () => {
            setPicked(undefined);
            onClose();
          },
        }
      )
    );
  };

  const handleClose = () => {
    setPicked(undefined);
    onClose();
  };

  const myDisplayName = me.data?.user
    ? `${me.data.user.firstName ?? ''} ${me.data.user.lastName ?? ''}`.trim() ||
      me.data.user.email ||
      'me'
    : 'me';

  // Org roster (cached by TanStack Query — `ServerSearchSelect` reuses the
  // same query) used to resolve the selected assignee id → a display name
  // for the closed-state trigger. Falls back to the current user's name.
  const { data: roster } = useAssigneeSearch('', open);
  const assigneeLabel =
    roster?.find((a) => a.id === assignedAccountId)?.fullName ??
    (assignedAccountId === myAccountId ? myDisplayName : null);

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalHeader onClose={handleClose} title="Add Task Set" />
      <div className={css.body}>
        <div className={css.field}>
          <Text as="span" size="sm" weight="medium">
            Assigned to
          </Text>
          <ServerSearchSelect<AssigneeSearchResult>
            getOptionLabel={(item) => item.fullName}
            getOptionValue={(item) => item.id}
            onChange={(id) => setAssignedAccountId(id ?? '')}
            placeholder="Select assignee"
            searchPlaceholder="Search by name…"
            selectedLabel={assigneeLabel}
            useSearch={useAssigneeSearch}
            usePortal
            value={assignedAccountId || null}
          />
        </div>

        <div className={css.field}>
          <Text as="span" size="sm" weight="medium">
            Itinerary
          </Text>
          {lockedItineraryId ? (
            <Text className={css.assigneeNote} size="sm">
              Locked to the current itinerary
            </Text>
          ) : (
            <Select
              onChange={(e) =>
                setPickedItineraryId(e.target.value || undefined)
              }
              options={itineraryOptions}
              usePortal
              value={pickedItineraryId ?? ''}
            />
          )}
        </div>

        {needsPick ? (
          <div className={css.field}>
            <Text as="span" size="sm" weight="medium">
              Collection
            </Text>
            <TaskCollectionSelect
              itineraryId={pickedItineraryId}
              onChange={(id) => setPicked(id)}
              usePortal
              value={picked}
            />
          </div>
        ) : null}

        {collectionId ? (
          <div>
            <Text as="p" className={css.previewTitle} size="sm" weight="medium">
              Tasks in this collection
            </Text>
            {templatesQuery.isLoading ? (
              <div className={css.previewEmpty}>Loading…</div>
            ) : (templatesQuery.data?.length ?? 0) === 0 ? (
              <div className={css.previewEmpty}>
                No tasks in this collection
              </div>
            ) : (
              <div className={css.previewTable}>
                <div className={css.previewHead}>
                  <div>Title</div>
                  <div>Profile</div>
                  <div>Due</div>
                </div>
                {templatesQuery.data?.map((t) => (
                  <div className={css.previewRow} key={t.id}>
                    <div className={css.previewRowTitle}>{t.title}</div>
                    <div>{t.profile}</div>
                    <div className={css.previewRowDue}>
                      Due date is <b>{t.duration ?? '—'}</b>{' '}
                      <b>{t.timerDirection ?? ''}</b> the{' '}
                      <b>
                        {(t.timerReference ?? '').split('_').join(' ') || '—'}
                      </b>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : null}

        {mutation.isError ? (
          <Text as="p" className={css.error} size="sm">
            {mutation.error?.message ?? 'Apply failed'}
          </Text>
        ) : null}
      </div>
      <ModalFooter>
        <Button
          disabled={mutation.isPending}
          onClick={handleClose}
          size="sm"
          variant="outline"
        >
          Cancel
        </Button>
        <Button
          disabled={mutation.isPending || !assignedAccountId || !collectionId}
          onClick={handleApply}
          size="sm"
          variant="primary"
        >
          {mutation.isPending ? 'Applying…' : 'Add'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
